// components/modals/UploadTranscriptModal.jsx
// A transcript file in, a meeting being processed out: one step.

import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { FiUpload, FiLoader, FiX } from 'react-icons/fi';

import * as meetingsApi from '../../services/meetingsApi';
import { toLocalInputValue } from '../../utils/dateTimeUtils';

const inputClass =
  'w-full rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-zinc-200 focus:border-[#5652CF] focus:outline-none';

// Dark, compact react-select styles.
const selectStyles = {
  control: (styles) => ({
    ...styles,
    backgroundColor: '#18181b', // zinc-900
    borderColor: '#3f3f46', // zinc-700
    boxShadow: 'none',
    '&:hover': { borderColor: '#52525b' }, // zinc-600
    minHeight: '36px',
    borderRadius: '0.375rem',
    fontSize: '0.75rem',
  }),
  menu: (styles) => ({
    ...styles,
    backgroundColor: '#18181b',
    borderColor: '#3f3f46',
    borderWidth: '1px',
    borderRadius: '0.375rem',
    zIndex: 60,
    padding: '0.25rem',
  }),
  option: (styles, { isFocused, isSelected }) => ({
    ...styles,
    backgroundColor: isSelected ? '#2563eb' : isFocused ? '#27272a' : '#18181b',
    color: 'white',
    cursor: 'pointer',
    fontSize: '0.75rem',
    padding: '0.375rem 0.5rem',
    borderRadius: '0.25rem',
  }),
  singleValue: (styles) => ({ ...styles, color: '#e4e4e7' }),
  multiValue: (styles) => ({
    ...styles,
    backgroundColor: '#27272a',
    borderRadius: '0.25rem',
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    color: '#e4e4e7',
    fontSize: '0.75rem',
  }),
  multiValueRemove: (styles) => ({
    ...styles,
    color: '#94a3b8',
    ':hover': { backgroundColor: '#ef4444', color: 'white' },
  }),
  input: (styles) => ({ ...styles, color: 'white', margin: 0, padding: 0 }),
  placeholder: (styles) => ({
    ...styles,
    color: '#71717a',
    fontSize: '0.75rem',
  }),
};

const UploadTranscriptModal = ({
  onClose,
  onUploaded,
  accessToken,
  users = [],
}) => {
  const [file, setFile] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(toLocalInputValue(new Date(), 10));
  const [startTime, setStartTime] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setIsUploading(true);
    try {
      await meetingsApi.uploadTranscript(accessToken, file, {
        title,
        date,
        // Local date and time, sent with its zone.
        startTime: startTime
          ? new Date(`${date}T${startTime}`).toISOString()
          : undefined,
        attendees: attendees.map((a) => a.email),
      });
      toast.success('Transcript uploaded. The meeting is being processed.');
      onUploaded();
    } catch (err) {
      toast.error(`Upload failed: ${err.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  return ReactDOM.createPortal(
    // eslint-disable-next-line
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-70 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* eslint-disable-next-line */}
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="text-p-text w-full max-w-md space-y-4 rounded-2xl bg-zinc-900 p-5 shadow-xl"
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">
            Upload transcript
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-full p-1 text-zinc-400 hover:bg-zinc-700 hover:text-white"
          >
            <FiX size={20} />
          </button>
        </div>
        <label className="block space-y-1 text-xs text-zinc-400">
          <span>Transcript (.vtt or .txt)</span>
          <input
            type="file"
            accept=".vtt,.txt"
            required
            onChange={(e) => setFile(e.target.files[0] || null)}
            className="w-full text-sm text-zinc-300 file:mr-4 file:rounded-md file:border-0 file:bg-zinc-700 file:px-4 file:py-2 file:text-xs file:font-semibold file:text-zinc-300 hover:file:bg-zinc-600"
          />
        </label>
        <label className="block space-y-1 text-xs text-zinc-400">
          <span>Title (optional)</span>
          <input
            type="text"
            value={title}
            placeholder="Defaults to the file name"
            onChange={(e) => setTitle(e.target.value)}
            className={inputClass}
          />
        </label>
        <div className="flex gap-3">
          <label className="block flex-1 space-y-1 text-xs text-zinc-400">
            <span>Meeting date</span>
            <input
              type="date"
              value={date}
              required
              onChange={(e) => setDate(e.target.value)}
              className={inputClass}
            />
          </label>
          <label className="block w-32 space-y-1 text-xs text-zinc-400">
            <span>Start time (optional)</span>
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className={inputClass}
            />
          </label>
        </div>
        <div className="space-y-1 text-xs text-zinc-400">
          <span id="upload-attendees">Who was there (optional)</span>
          <Select
            isMulti
            aria-labelledby="upload-attendees"
            options={users
              .filter((u) => u.email)
              .map((u) => ({ ...u, value: u.email, label: u.displayName }))}
            value={attendees}
            onChange={(selected) => setAttendees(selected || [])}
            styles={{
              ...selectStyles,
              menuPortal: (base) => ({ ...base, zIndex: 70 }),
            }}
            menuPortalTarget={document.body}
            placeholder="People who were in it..."
          />
          <p className="text-[11px] text-zinc-500">
            Only you, these people and the speakers we recognise can see this
            meeting.
          </p>
        </div>
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!file || isUploading}
            className="flex h-9 items-center justify-center gap-2 rounded-md bg-[#5652CF] px-4 text-xs font-semibold text-white transition-colors hover:bg-[#4642bf] disabled:bg-zinc-700"
          >
            {isUploading ? (
              <FiLoader className="animate-spin" />
            ) : (
              <>
                <FiUpload size={14} /> Upload and process
              </>
            )}
          </button>
        </div>
      </form>
    </div>,
    document.getElementById('modal-root') || document.body,
  );
};

export default UploadTranscriptModal;
